import { Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { motion, AnimatePresence } from "motion/react";

import { Logo } from "./Logo";
import { supabase } from "@/integrations/supabase/client";

const links = [
  { to: "/services", label: "Services" },
  { to: "/pricing", label: "Pricing" },
  { to: "/industries", label: "Industries" },
  { to: "/how-it-works", label: "How it works" },
  { to: "/about", label: "About" },
] as const;

export function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [signedIn, setSignedIn] = useState(false);

  useEffect(() => {
    void supabase.auth.getSession().then(({ data }) => setSignedIn(Boolean(data.session)));
    const { data: sub } = supabase.auth.onAuthStateChange((_e, session) =>
      setSignedIn(Boolean(session)),
    );
    return () => sub.subscription.unsubscribe();
  }, []);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <header
      className={`fixed top-0 z-50 w-full border-b transition-[height,background-color,border-color,box-shadow] duration-200 ease-out ${
        scrolled
          ? "h-16 border-brand-navy/10 bg-background/80 backdrop-blur-xl backdrop-saturate-180 shadow-xs"
          : "h-20 border-transparent bg-background/55 backdrop-blur-md backdrop-saturate-150"
      }`}
    >
      <nav className="mx-auto flex h-full max-w-7xl items-center justify-between px-6">
        <Logo onClick={() => setOpen(false)} />

        <div className="hidden items-center gap-8 text-sm font-medium text-brand-navy/70 md:flex">
          {links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              className="transition-colors hover:text-brand-accent active:scale-95 duration-100"
              activeProps={{ className: "text-brand-accent" }}
            >
              {l.label}
            </Link>
          ))}
          <Link
            to={signedIn ? "/dashboard" : "/auth"}
            className="transition-colors hover:text-brand-accent active:scale-95 duration-100"
          >
            {signedIn ? "Dashboard" : "Sign in"}
          </Link>
          <Link
            to="/contact"
            className="rounded-full bg-brand-navy px-5 py-2.5 text-background transition-colors hover:bg-brand-accent active:scale-[0.97] active:duration-75 shadow-xs"
          >
            Get Started
          </Link>
        </div>

        <button
          type="button"
          aria-label={open ? "Close menu" : "Open menu"}
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
          className="rounded-lg p-1.5 text-brand-navy active:scale-90 transition-transform md:hidden"
        >
          {open ? <X className="size-6" /> : <Menu className="size-6" />}
        </button>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ type: "spring", bounce: 0, duration: 0.32 }}
            className="overflow-hidden border-t border-brand-navy/10 bg-background/95 backdrop-blur-2xl backdrop-saturate-180 px-6 py-6 shadow-xl md:hidden"
          >
            <div className="flex flex-col gap-4 text-base font-medium text-brand-navy">
              {links.map((l) => (
                <Link
                  key={l.to}
                  to={l.to}
                  onClick={() => setOpen(false)}
                  className="rounded-lg py-1 transition-colors hover:text-brand-accent active:scale-[0.98]"
                >
                  {l.label}
                </Link>
              ))}
              <Link
                to={signedIn ? "/dashboard" : "/auth"}
                onClick={() => setOpen(false)}
                className="rounded-lg py-1 transition-colors hover:text-brand-accent active:scale-[0.98]"
              >
                {signedIn ? "Dashboard" : "Sign in"}
              </Link>
              <Link
                to="/contact"
                onClick={() => setOpen(false)}
                className="mt-2 rounded-full bg-brand-navy px-5 py-3 text-center text-background font-semibold transition-colors hover:bg-brand-accent active:scale-[0.97]"
              >
                Get Started
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
